import { Box, Grid, Paper, Typography } from '@mui/material';
import { useMemo } from 'react';
import { formatMoney } from './AnalyticsKpiCard';
import { analyticsColors, analyticsPanelSx } from './analyticsStyles';
import { SalesPeriodToggle } from './SalesPeriodToggle';
import { SalesMetricCard } from './SalesMetricCard';
import { OrdersBarChart, RevenueTrendChart } from './AnalyticsCharts';
import { TimePeriod } from '../../../enum';
import { getChartPointCount, getCustomRangeDayCount } from './salesDurationViewConfig';
import {
  alignPreviousSeries,
  alignSeriesToPeriodDays,
  computeOrdersPerPeriod,
  ordersPerPeriodLabel,
  prepareTrendSeriesForView,
  SalesGranularity,
} from './salesAnalyticsUtils';

interface SalesData {
  revenue: number;
  orders: number;
  averageOrderValue: number;
  growthRate: number | null;
  periodDays: number;
}

interface Props {
  sales?: SalesData;
  salesView: SalesGranularity;
  onSalesViewChange: (view: SalesGranularity) => void;
  availableViews: SalesGranularity[];
  timeDuration: string;
  customRangeStart?: Date | null;
  customRangeEnd?: Date | null;
  revenueTrend?: { current: any[]; previous: any[] };
  ordersTrend?: any[];
}
export const SalesAnalyticsSection = ({
  sales,
  salesView,
  onSalesViewChange,
  availableViews,
  timeDuration,
  customRangeStart,
  customRangeEnd,
  revenueTrend,
  ordersTrend,
}: Props) => {
  const isSingleDayChart =
    timeDuration === TimePeriod.today || timeDuration === TimePeriod.yesterDay;
  const showPreviousPeriod = !isSingleDayChart;
  const growthRate = sales?.growthRate ?? null;

  const effectivePeriodDays =
    timeDuration === TimePeriod.customRange && customRangeStart && customRangeEnd
      ? getCustomRangeDayCount(customRangeStart, customRangeEnd)
      : sales?.periodDays;

  const growthDisplay =
    growthRate !== null && growthRate !== undefined
      ? `${growthRate > 0 ? '+' : ''}${growthRate}%`
      : '—';

  const chartPointCount = getChartPointCount(
    timeDuration,
    effectivePeriodDays,
    salesView,
    customRangeStart,
    customRangeEnd
  );

  const dailyRevenue = useMemo(() => {
    const current = alignSeriesToPeriodDays(
      revenueTrend?.current,
      effectivePeriodDays,
      'daily'
    );
    const previous = alignSeriesToPeriodDays(
      revenueTrend?.previous,
      effectivePeriodDays,
      'daily'
    );
    return { current, previous };
  }, [revenueTrend, effectivePeriodDays]);

  const dailyOrders = useMemo(
    () =>
      alignSeriesToPeriodDays(ordersTrend, effectivePeriodDays, 'daily'),
    [ordersTrend, effectivePeriodDays]
  );

  // Timeline must match the selected period start/end, not truncated chart buckets.
  const periodTimelineStart = useMemo(() => {
    if (customRangeStart && timeDuration === TimePeriod.customRange) {
      return customRangeStart;
    }
    const dateStr = dailyRevenue.current[0]?.date;
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  }, [customRangeStart, timeDuration, dailyRevenue.current]);

  const periodTimelineEnd = useMemo(() => {
    if (customRangeEnd && timeDuration === TimePeriod.customRange) {
      return customRangeEnd;
    }
    const dateStr =
      dailyRevenue.current[dailyRevenue.current.length - 1]?.date;
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  }, [customRangeEnd, timeDuration, dailyRevenue.current]);

  const aggregatedRevenue = useMemo(() => {
    const current = alignSeriesToPeriodDays(
      prepareTrendSeriesForView(
        dailyRevenue.current,
        salesView,
        'value',
        chartPointCount
      ),
      chartPointCount,
      salesView
    );

    const previous = alignPreviousSeries(
      current,
      alignSeriesToPeriodDays(
        prepareTrendSeriesForView(
          dailyRevenue.previous,
          salesView,
          'value',
          chartPointCount
        ),
        chartPointCount,
        salesView
      ),
      'value'
    );

    return { current, previous };
  }, [dailyRevenue, salesView, chartPointCount]);

  const aggregatedOrders = useMemo(
    () =>
      alignSeriesToPeriodDays(
        prepareTrendSeriesForView(
          dailyOrders,
          salesView,
          'count',
          chartPointCount
        ),
        chartPointCount,
        salesView
      ),
    [dailyOrders, salesView, chartPointCount]
  );

  const ordersChartData = useMemo(
    () =>
      aggregatedOrders.map((point, index) => ({
        ...point,
        revenue: aggregatedRevenue.current[index]?.value ?? 0,
      })),
    [aggregatedOrders, aggregatedRevenue.current]
  );

  const ordersPerPeriod = sales
    ? computeOrdersPerPeriod(
        sales.orders,
        effectivePeriodDays ?? sales.periodDays,
        salesView,
        chartPointCount
      )
    : 0;

  const growthPositive =
    growthRate !== null && growthRate !== undefined && growthRate > 0;
  const growthNegative =
    growthRate !== null && growthRate !== undefined && growthRate < 0;

  return (
    <Paper elevation={0} sx={{ ...analyticsPanelSx, mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: analyticsColors.title,
          }}
        >
          Sales Analytics
        </Typography>
        <SalesPeriodToggle
          value={salesView}
          onChange={onSalesViewChange}
          availableViews={availableViews}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)',
          },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <SalesMetricCard label='Revenue' value={formatMoney(sales?.revenue || 0)} />
        <SalesMetricCard label='Orders' value={sales?.orders || 0} />
        <SalesMetricCard
          label='Avg Order Value'
          value={formatMoney(sales?.averageOrderValue || 0)}
        />
        <SalesMetricCard
          label={ordersPerPeriodLabel(salesView)}
          value={ordersPerPeriod}
        />
        <SalesMetricCard
          label='Growth Rate'
          value={growthDisplay}
          highlight={growthPositive}
          negative={growthNegative}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RevenueTrendChart
            current={aggregatedRevenue.current}
            previous={aggregatedRevenue.previous}
            granularity={salesView}
            showPreviousPeriod={showPreviousPeriod}
            timeDuration={timeDuration}
            periodDays={effectivePeriodDays}
            customRangeStart={periodTimelineStart}
            customRangeEnd={periodTimelineEnd}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <OrdersBarChart
            data={ordersChartData}
            granularity={salesView}
            timeDuration={timeDuration}
            periodDays={effectivePeriodDays}
            customRangeStart={periodTimelineStart}
            customRangeEnd={periodTimelineEnd}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
